import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useStudioUser } from "../../hooks/useStudioUser";
import { useStudioPlan } from "../../hooks/useStudioPlan";
import UpgradePrompt from "../UpgradePrompt";
import Notification from "../Notification";
import PasswordStrengthIndicator from "../PasswordStrengthIndicator";
import { createClient } from "@supabase/supabase-js";
import {
  isPasswordValid,
  validatePassword,
} from "../../utils/passwordValidation";
import {
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import bcrypt from "bcryptjs";

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  email: 254,
  password: 128, // Reasonable max length for passwords
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // Remove dangerous patterns
  sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
  sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
  sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
  sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
  sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
  
  return sanitized.trim();
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'email':
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailRegex.test(sanitized) && !sanitized.includes('..');
    case 'password':
      // Password validation is handled by the existing password validation functions
      return sanitized.length >= 1;
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  console.warn(`Security Event: ${event}`, {
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    details
  });
};

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  process.env.NEXT_PUBLIC_SUPABASE_URL ||
  "";
const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY ||
  "";
const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Common passwords to prevent usage
const COMMON_PASSWORDS = [
  "123456",
  "password",
  "123456789",
  "12345678",
  "12345",
  "1234567",
  "1234567890",
  "qwerty",
  "abc123",
  "million2",
  "000000",
  "1234",
  "iloveyou",
  "aaron431",
  "password1",
  "qqww1122",
  "123123",
  "omgpop",
  "123321",
  "654321",
  "qwertyuiop",
  "qwer1234",
  "123qwe",
  "zxcvbnm",
  "1q2w3e4r",
  "baseball",
  "football",
  "welcome",
  "monkey",
  "login",
  "starwars",
  "123abc",
  "dragon",
  "passw0rd",
  "master",
  "hello",
  "freedom",
  "whatever",
  "qazwsx",
  "trustno1",
  "jordan23",
  "harley",
  "password123",
  "hunter",
  "solo",
  "sunshine",
  "iloveyou1",
  "princess",
  "admin",
  "welcome123",
  "666666",
  "hottie",
  "maggie",
  "jordan",
  "butterfly",
  "555555",
  "arsenal",
  "shadow",
  "killer",
  "tigger",
  "hockey",
  "charlie",
  "freedom1",
  "michelle",
  "babygirl",
  "taylor",
  "purple",
  "nicole",
  "basketball",
  "angels",
  "flowers",
  "summer",
  "stupid",
  "slipknot",
  "orange",
  "football1",
  "enter",
  "computer",
  "cheese",
  "soccer",
  "natasha",
  "mickey",
  "secret",
  "summer1",
  "ncc1701",
  "11111111",
  "87654321",
  "abcd1234",
  "1qaz2wsx",
  "administrator",
  "changeme",
  "letmein",
  "guestpass",
  "test",
  "guest",
];

// Utility function for consistent input styling
const getInputClassName = (baseClasses: string) => {
  return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-indigo-500 focus:border-indigo-500 dark:focus:ring-indigo-400 dark:focus:border-indigo-400`;
};

function SettingsPlanLoadingSection({ title }: { title: string }) {
  return (
    <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 w-full">
      <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
        {title}
      </h2>
      <div className="flex justify-center items-center py-8">
        <div className="animate-pulse flex space-x-4 items-center">
          <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
          <div className="text-sm text-gray-600 dark:text-gray-400">Loading...</div>
        </div>
      </div>
    </section>
  );
}

export default function SecuritySettings() {
  const location = useLocation();
  const { session, user, isSubAccount } = useAuth();
  const { studioUser } = useStudioUser();
  const { hasCapability, loading: planLoading } = useStudioPlan();
  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);
  
  const [kioskPassword, setKioskPassword] = useState("");
  const [kioskPasswordLoading, setKioskPasswordLoading] = useState(false);
  const [kioskPasswordTouched, setKioskPasswordTouched] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showKioskPassword, setShowKioskPassword] = useState(false);
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });

  // Change Email State
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Single useEffect to fetch CSRF token once on component mount
  useEffect(() => {
    fetchCsrfToken();
  }, []); // Empty dependency array - only run once

  // Enhanced email validation
  const validateEmail = (email: string) => {
    if (!email.trim()) {
      return "Email address is required";
    }

    // Security validation first
    if (!validateInput(email, 'email')) {
      return "Email contains invalid characters";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return "Please enter a valid email address";
    }

    // Check if email is different from current
    const currentEmail =
      !isSubAccount && user && "email" in user ? (user as any).email : null;
    if (email.trim().toLowerCase() === currentEmail?.toLowerCase()) {
      return "New email must be different from current email";
    }

    // Additional email format checks
    const emailParts = email.split("@");
    if (emailParts.length !== 2) {
      return "Please enter a valid email address";
    }

    const [localPart, domain] = emailParts;
    if (!localPart || !domain || domain.split(".").length < 2) {
      return "Please enter a valid email address";
    }

    return null;
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'changeEmail' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Validate email before submitting
    const validationError = validateEmail(newEmail);
    if (validationError) {
      logSecurityEvent('INVALID_EMAIL_ATTEMPT', { 
        email: newEmail.substring(0, 50),
        error: validationError 
      });
      setNotification({
        show: true,
        message: validationError,
        title: "Validation Error",
      });
      return;
    }

    // Security validation
    if (!validateInput(newEmail, 'email')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { 
        field: 'email', 
        value: newEmail.substring(0, 50) 
      });
      setNotification({
        show: true,
        message: "Invalid email format detected",
        title: "Security Error",
      });
      return;
    }

    setEmailLoading(true);
    setLastSubmissionTime(now);

    try {
      // Pre-check: ensure email isn't already in use by another web app account
      const checkResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/check-email-exists`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: sanitizeInput(newEmail, 'email') }),
        }
      );
      if (checkResponse.ok) {
        const checkData = await checkResponse.json();
        if (checkData.exists) {
          setEmailLoading(false);
          setNotification({
            show: true,
            message: "That email address is already associated with another account. Please use a different email.",
            title: "Email Already In Use",
          });
          return;
        }
      }

      const { error } = await supabase.auth.updateUser({
        email: sanitizeInput(newEmail, 'email'),
      });
      if (error) {
        // Handle specific Supabase errors with user-friendly messages
        if (error.message.includes("rate limit")) {
          throw new Error(
            "Too many requests. Please wait a few minutes before trying again."
          );
        } else if (
          error.message.includes("already registered") ||
          error.message.includes("already exists") ||
          error.message.includes("already been registered")
        ) {
          setEmailLoading(false);
          setNotification({
            show: true,
            message: "That email address is already associated with another account. Please use a different email.",
            title: "Email Already In Use",
          });
          return;
        } else {
          throw error;
        }
      }
      setNewEmail("");
      setEmailTouched(false);
      setNotification({
        show: true,
        message: "Please check your new email address to confirm the change.",
        title: "Success",
      });
    } catch (err) {
      logSecurityEvent('EMAIL_UPDATE_FAILED', { 
        error: err instanceof Error ? err.message : 'Unknown error',
        email: newEmail.substring(0, 50)
      });
      setNotification({
        show: true,
        message: err instanceof Error ? err.message : "Failed to update email",
        title: "Error",
      });
    } finally {
      setEmailLoading(false);
    }
  };

  // Enhanced password validation with common password check
  const validateKioskPassword = (password: string) => {
    if (!password) {
      return "Password is required";
    }

    // Security validation first
    if (!validateInput(password, 'password')) {
      return "Password contains invalid characters";
    }

    // Check against common passwords first
    if (COMMON_PASSWORDS.includes(password.toLowerCase())) {
      return "This password is too common and easily guessed. Please choose a more unique password.";
    }

    // Then check standard password requirements
    if (!isPasswordValid(password)) {
      return "Password does not meet security requirements";
    }

    return null;
  };

  const handleKioskPasswordSubmit = () => {
    // Validate password
    const passwordError = validateKioskPassword(kioskPassword);

    if (passwordError) {
      logSecurityEvent('INVALID_PASSWORD_ATTEMPT', { 
        error: passwordError,
        passwordLength: kioskPassword.length 
      });
      setNotification({
        show: true,
        message: passwordError,
        title: "Validation Error",
      });
      return;
    }

    // Security validation
    if (!validateInput(kioskPassword, 'password')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { 
        field: 'password', 
        passwordLength: kioskPassword.length 
      });
      setNotification({
        show: true,
        message: "Invalid password format detected",
        title: "Security Error",
      });
      return;
    }

    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleKioskPasswordChange = async () => {
    if (!studioUser?.studio_id) return;

    // Rate limiting check
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before submitting again",
        title: "Error",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'updateKioskPassword' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Additional client-side validation
    if (!session?.access_token) {
      logSecurityEvent('AUTHENTICATION_MISSING', { action: 'updateKioskPassword' });
      setNotification({
        show: true,
        message: "Authentication required. Please log in again.",
        title: "Error",
      });
      return;
    }

    const passwordError = validateKioskPassword(kioskPassword);
    if (passwordError) {
      logSecurityEvent('INVALID_PASSWORD_ATTEMPT', { 
        error: passwordError,
        passwordLength: kioskPassword.length 
      });
      setNotification({
        show: true,
        message: passwordError,
        title: "Validation Error",
      });
      return;
    }

    // Security validation
    if (!validateInput(kioskPassword, 'password')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { 
        field: 'password', 
        passwordLength: kioskPassword.length 
      });
      setNotification({
        show: true,
        message: "Invalid password format detected",
        title: "Security Error",
      });
      return;
    }

    setKioskPasswordLoading(true);
    setShowConfirmDialog(false);
    setLastSubmissionTime(now);

    try {
      // Hash password on client side for secure transmission
      const hashedPassword = await bcrypt.hash(kioskPassword, 12);

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/${studioUser.studio_id}/kiosk-password`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            kiosk_password_hash: hashedPassword,
            client_hashed: true, // Flag to indicate client-side hashing
          }),
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update kiosk password");
      }
      setKioskPassword("");
      setKioskPasswordTouched(false);
      setNotification({
        show: true,
        message: "Kiosk password updated successfully!",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('KIOSK_PASSWORD_UPDATE_FAILED', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        studioId: studioUser.studio_id
      });
      setNotification({
        show: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to update kiosk password. Please try again.",
        title: "Error",
      });
    } finally {
      setKioskPasswordLoading(false);
    }
  };

  useEffect(() => {
    if (planLoading || location.hash !== "#kiosk-password-section") {
      return;
    }

    const scrollToKioskPassword = () => {
      document.getElementById("kiosk-password-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    };

    const frameId = window.requestAnimationFrame(scrollToKioskPassword);
    return () => window.cancelAnimationFrame(frameId);
  }, [planLoading, location.hash]);

  return (
    <div className="space-y-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />

        {/* Change Email Address Section - Only for main accounts */}
        {!isSubAccount && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
                Change Email Address
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Enter a new email address. You will need to confirm the change
                via a link sent to your new email.
              </p>
            </div>
            <form
              onSubmit={handleChangeEmail}
              className="space-y-4 max-w-md mt-4"
            >
              {/* Show current email */}
              <div className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                Current Email:{" "}
                {!isSubAccount && user && "email" in user
                  ? (user as any).email
                  : "-"}
              </div>
              <div>
                <label
                  htmlFor="new-email"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  New Email Address
                </label>
                <input
                  type="email"
                  id="new-email"
                  value={newEmail}
                  onChange={(e) => {
                    setNewEmail(e.target.value);
                    if (!emailTouched) setEmailTouched(true);
                  }}
                  onBlur={() => setEmailTouched(true)}
                  required
                  maxLength={MAX_LENGTHS.email}
                  className={getInputClassName(
                    `mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                      emailTouched && newEmail && validateEmail(newEmail)
                        ? "border-red-300 dark:border-red-500 focus:border-red-500 dark:focus:border-red-400 focus:ring-red-500 dark:focus:ring-red-400"
                        : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400"
                    }`
                  )}
                  placeholder="Enter new email address"
                />
                {emailTouched && newEmail && validateEmail(newEmail) && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                    {validateEmail(newEmail)}
                  </p>
                )}
              </div>
              <div>
                <button
                  type="submit"
                  disabled={emailLoading}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="pointer-events-none">{emailLoading ? "Updating..." : "Update Email"}</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Sub-account notice */}
        {isSubAccount && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              You are logged in as a sub-account. Email changes are not
              available for sub-accounts.
            </p>
          </div>
        )}

        <div id="kiosk-password-section">
        {planLoading ? (
          <SettingsPlanLoadingSection title="Kiosk Password" />
        ) : !hasCapability("kiosk") ? (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4">
              Kiosk Password
            </h3>
            <UpgradePrompt capability="kiosk" hideTitle />
          </div>
        ) : (
        <>
        {/* Kiosk Password Section */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white">
              Kiosk Password
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Set a password for the student sign-in kiosk.
            </p>
          </div>

          <div className="space-y-4 max-w-md mt-4">
            <div>
              <label
                htmlFor="kiosk-password"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                New Kiosk Password
              </label>
              <div className="relative">
                <input
                  type={showKioskPassword ? "text" : "password"}
                  id="kiosk-password"
                  value={kioskPassword}
                  onChange={(e) => {
                    setKioskPassword(e.target.value);
                    if (!kioskPasswordTouched) setKioskPasswordTouched(true);
                    setShowPasswordRequirements(true);
                  }}
                  onFocus={() => setShowPasswordRequirements(true)}
                  onBlur={() => setKioskPasswordTouched(true)}
                  maxLength={MAX_LENGTHS.password}
                  className={getInputClassName(
                    `mt-1 block w-full rounded-md shadow-sm pr-10 sm:text-sm ${
                      kioskPasswordTouched &&
                      kioskPassword &&
                      validateKioskPassword(kioskPassword)
                        ? "border-red-300 dark:border-red-500 focus:border-red-500 dark:focus:border-red-400 focus:ring-red-500 dark:focus:ring-red-400"
                        : "border-gray-300 dark:border-gray-600 focus:border-indigo-500 dark:focus:border-indigo-400"
                    }`
                  )}
                  placeholder="Enter a strong password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center mt-1 p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition text-gray-500 dark:text-gray-400"
                  onClick={() => setShowKioskPassword(!showKioskPassword)}
                >
                  {showKioskPassword ? (
                    <EyeSlashIcon className="h-5 w-5 pointer-events-none" />
                  ) : (
                    <EyeIcon className="h-5 w-5 pointer-events-none" />
                  )}
                </button>
              </div>

              {/* Password validation error */}
              {kioskPasswordTouched &&
                kioskPassword &&
                validateKioskPassword(kioskPassword) && (
                  <div className="mt-2 flex items-center text-sm text-red-600 dark:text-red-400">
                    <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                    {validateKioskPassword(kioskPassword)}
                  </div>
                )}
            </div>

            {/* Password Requirements Indicator */}
            {showPasswordRequirements && kioskPassword && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                <PasswordStrengthIndicator
                  password={kioskPassword}
                  showRequirements={true}
                />
              </div>
            )}

            {/* Security Tips */}
            {!kioskPasswordTouched && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  <strong>Security Best Practices:</strong>
                </p>
                <ul className="mt-1 text-sm text-blue-700 dark:text-blue-300 list-disc list-inside space-y-1">
                  <li>Use a unique password that hasn't been used elsewhere</li>
                  <li>
                    Avoid common passwords like "password", "123456", or
                    "qwerty"
                  </li>
                  <li>
                    Include a mix of uppercase, lowercase, numbers, and symbols
                  </li>
                  <li>Make it at least 8 characters long</li>
                  <li>
                    Don't use personal information like names or birthdays
                  </li>
                </ul>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={handleKioskPasswordSubmit}
                disabled={kioskPasswordLoading || !kioskPassword}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="pointer-events-none">{kioskPasswordLoading ? "Updating..." : "Update Kiosk Password"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Confirmation Dialog */}
        {showConfirmDialog && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900 sm:mx-0 sm:h-10 sm:w-10">
                    <svg
                      className="h-6 w-6 text-yellow-600 dark:text-yellow-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 18.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Update Kiosk Password
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to update the kiosk password? This
                        will affect all student sign-in stations.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={handleKioskPasswordChange}
                    disabled={kioskPasswordLoading}
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-indigo-600 dark:text-indigo-400 shadow-sm hover:text-indigo-800 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="pointer-events-none">{kioskPasswordLoading ? "Updating..." : "Update Password"}</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowConfirmDialog(false)}
                    disabled={kioskPasswordLoading}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        </>
        )}
        </div>
    </div>
  );
}
